import * as React from 'react';
import { Check, Search, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { type TestIdProps, testIdProps } from '../../lib/test-id';
import { Input } from '../atoms/input';
import { Badge } from '../atoms/badge';
import { Checkbox } from './checkbox';

export type SelectPanelOption = {
  value: string;
  label: string;
  group?: string;
  children?: SelectPanelOption[];
  disabled?: boolean;
};

export interface SelectPanelProps extends React.HTMLAttributes<HTMLDivElement>, TestIdProps {
  options: SelectPanelOption[];
  value?: string | string[];
  onValueChange?: (value: string | string[]) => void;
  multiple?: boolean;
  searchable?: boolean;
  nested?: boolean;
  bulkSelect?: boolean;
  tagBased?: boolean;
  placeholder?: string;
}

function flattenOptions(options: SelectPanelOption[]): SelectPanelOption[] {
  return options.flatMap((o) => (o.children?.length ? [o, ...flattenOptions(o.children)] : [o]));
}

const SelectPanel = React.forwardRef<HTMLDivElement, SelectPanelProps>(
  (
    {
      className,
      options,
      value,
      onValueChange,
      multiple = false,
      searchable = false,
      nested = false,
      bulkSelect = false,
      tagBased = false,
      placeholder = 'Search…',
      testId,
      ...props
    },
    ref,
  ) => {
    const [query, setQuery] = React.useState('');
    const flatOptions = React.useMemo(() => flattenOptions(options), [options]);
    const selected = React.useMemo(() => {
      if (multiple) return new Set(Array.isArray(value) ? value : []);
      return new Set(typeof value === 'string' && value ? [value] : []);
    }, [value, multiple]);

    const filtered = React.useMemo(() => {
      const leaves = nested
        ? flatOptions.filter((o) => !o.children?.length)
        : options.filter((o) => !o.children?.length);
      if (!query.trim()) return leaves;
      const q = query.toLowerCase();
      return leaves.filter(
        (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q),
      );
    }, [flatOptions, options, query, nested]);

    const groups = React.useMemo(() => {
      const map = new Map<string, SelectPanelOption[]>();
      for (const opt of filtered) {
        if (opt.children) continue;
        const key = opt.group ?? '';
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(opt);
      }
      return map;
    }, [filtered]);

    const toggle = (v: string) => {
      if (multiple) {
        const next = new Set(selected);
        if (next.has(v)) next.delete(v);
        else next.add(v);
        onValueChange?.([...next]);
      } else {
        onValueChange?.(v);
      }
    };

    const allValues = filtered.filter((o) => !o.children).map((o) => o.value);
    const allSelected = multiple && allValues.length > 0 && allValues.every((v) => selected.has(v));
    const someSelected = multiple && selected.size > 0 && !allSelected;
    const selectAllId = React.useId();

    function renderSelectionIndicator(isOn: boolean) {
      if (multiple) {
        return (
          <span
            aria-hidden
            className={cn(
              'flex size-4 items-center justify-center rounded-[0.1875rem] border border-border',
              isOn && 'border-border-brand bg-brand text-foreground-on-brand',
            )}
          >
            {isOn && <Check className="size-3" strokeWidth={3} />}
          </span>
        );
      }

      return (
        <span className="flex size-4 items-center justify-center">
          {isOn && <Check className="size-3.5 text-foreground" strokeWidth={3} />}
        </span>
      );
    }

    return (
      <div
        ref={ref}
        className={cn(
          'flex w-full max-w-sm flex-col gap-2 rounded-md border border-border bg-canvas p-3 shadow-md',
          className,
        )}
        {...props}
        {...testIdProps(testId)}
      >
        {searchable && (
          <Input
            type="search"
            placeholder={placeholder}
            aria-label={placeholder}
            value={query}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
          />
        )}

        {tagBased && multiple && selected.size > 0 && (
          <div className="flex flex-wrap gap-1">
            {[...selected].map((v) => {
              const opt = flatOptions.find((o) => o.value === v);
              return (
                <Badge key={v} color="accent" variant="primary">
                  {opt?.label ?? v}
                  <button
                    type="button"
                    className="ml-space-4 cursor-pointer rounded-full hover:bg-hover"
                    onClick={() => toggle(v)}
                    aria-label="Remove"
                  >
                    <X className="size-3" />
                  </button>
                </Badge>
              );
            })}
          </div>
        )}

        {bulkSelect && multiple && (
          <Checkbox
            id={selectAllId}
            label="Select all"
            checked={allSelected ? true : someSelected ? 'indeterminate' : false}
            onCheckedChange={(c) => {
              const next = new Set(selected);
              if (c === true) {
                for (const v of allValues) next.add(v);
                onValueChange?.([...next]);
                return;
              }
              for (const v of allValues) next.delete(v);
              onValueChange?.([...next]);
            }}
          />
        )}

        <div className="flex max-h-64 flex-col gap-0.5 overflow-auto">
          {[...groups.entries()].map(([group, items]) => (
            <div key={group || 'default'} className="flex flex-col gap-0.5">
              {group && (
                <div className="px-2 py-1 font-mono text-xs tracking-wider text-tertiary uppercase">
                  {group}
                </div>
              )}
              {items.map((opt) => {
                const isOn = selected.has(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={opt.disabled}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm text-muted-foreground hover:bg-surface-floating hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50',
                      isOn && 'bg-brand-subtle text-foreground-brand',
                      nested && opt.group && 'pl-4',
                    )}
                    onClick={() => toggle(opt.value)}
                  >
                    {renderSelectionIndicator(isOn)}
                    {!multiple && searchable && (
                      <Search className="size-3 text-quaternary opacity-0" aria-hidden />
                    )}
                    <span className="flex-1 truncate">{opt.label}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  },
);
SelectPanel.displayName = 'SelectPanel';

export { SelectPanel };
